import { useState, type ReactNode } from "react";

import { ChevronDown, ChevronUp, Link2, Send, Signature, Unlink, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { UserMultiSelect } from "@/components/ui/user-multi-select";
import { UserAvatar } from "@/components/ui/user-avatar";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import type { UserProfile } from "@/domains/pm/types";

import {
  chainUserIds,
  chainSize,
  mergeWithPrevious,
  moveGroup,
  removeSigner,
  signerDisplayName,
  splitSigner,
  syncChain,
  MAX_CHAIN_INVITEES,
  type SignerChain,
} from "../../signing/signerChain";

interface SignerChainEditorProps {
  /** Candidates — the project's members. Empty while loading, or with no project. */
  users: UserProfile[];
  chain: SignerChain;
  onChange: (chain: SignerChain) => void;
  isLoading: boolean;
  /** No project in scope: there is no member list, so only self-signing is offered. */
  hasProject: boolean;
  /**
   * The creator signs first but is not stored in `chain`; they are the fixed
   * first row. REQUIRED, and not nullable: `toSignerPayload` always puts them at
   * position 1, so a chain composed without a known identity would be displayed
   * one step out of line with the session it creates — and would be dropped from
   * the request entirely. A caller without a profile must not render this — and
   * because the app builds with `strictNullChecks: false`, the type alone does
   * not enforce that, so the component checks at runtime too.
   */
  currentUser: UserProfile;
  disabled?: boolean;
}

/** The chain's first step is position 2 on the wire — position 1 is the creator,
 *  unconditionally (`toSignerPayload`). Displayed numbers must say the same. */
const CHAIN_POSITION_OFFSET = 2;

/**
 * The ordered signer picker.
 *
 * Order is the whole point, so it is edited as a list of STEPS rather than a
 * flat multi-select: one step per position, and a step holding more than one
 * person is a parallel group who sign in any order (the chain waits for all of
 * them). Membership is still the shared `UserMultiSelect` — reusing it keeps
 * search, avatars and untick-to-remove identical to every other picker in the
 * app; this list only owns the ordering on top.
 *
 * The creator is NOT a step. They sign first, in the dialog they are standing
 * in, the moment they press Sign — so this list is only where the document goes
 * afterwards, and an empty list means they sign alone.
 */
export function SignerChainEditor({
  users,
  chain,
  onChange,
  isLoading,
  hasProject,
  currentUser,
  disabled = false,
}: SignerChainEditorProps) {
  const { t } = useLanguage();
  const copy = t.signing.dialog.signers;
  // Opened by the user, or already open because a chain exists (re-render after
  // adding someone, or a caller that seeded one).
  const [expanded, setExpanded] = useState(chain.length > 0);
  // "Sign myself" empties the chain, and an order takes real work to compose —
  // so the last one is kept here and handed back if they switch straight back.
  // Only the parent's `chain` is ever sent; this is a held copy, not a source.
  // It is a ONE-SHOT undo of that one click, cleared the moment it is handed
  // back: kept, it would outlive the order it holds and re-add, on the next
  // "Sign myself" round trip, signers the user had since removed by hand.
  const [stashedChain, setStashedChain] = useState<SignerChain>([]);
  // The prop's type says this cannot happen; `strictNullChecks: false` says the
  // compiler will never hold anyone to that. Rendering anyway would draw a
  // phantom "Step 1 — You (Unknown)" over a chain the caller cannot send, so
  // fail closed: no identity, no picker. Nullish, not falsy, so this agrees
  // exactly with the two checks the caller makes (`currentUserId != null` for
  // the picker and for `creatorUnknown`): an id of `0` is a real identity that
  // `toSignerPayload` sends, and refusing it here alone would leave a Sign
  // button enabled over a chain the user cannot see.
  if (currentUser?.id == null) return null;
  const byId = new Map(users.map((user) => [user.id, user]));
  const selectedIds = chainUserIds(chain);
  // The creator takes one of the backend's ten, so the picker stops at nine.
  const atCap = chainSize(chain) >= MAX_CHAIN_INVITEES;
  const isSendingToOthers = expanded || chain.length > 0;

  const modePicker = (
    <div role="group" aria-label={copy.modeLabel} className="grid gap-3 sm:grid-cols-2">
      <button
        type="button"
        aria-pressed={!isSendingToOthers}
        className={cn(
          "flex min-h-24 items-start gap-3 rounded-xl border-2 p-4 text-left transition-colors",
          !isSendingToOthers
            ? "border-primary bg-primary/[0.03]"
            : "border-border hover:border-muted-foreground/40 hover:bg-muted/30"
        )}
        disabled={disabled}
        onClick={() => {
          setExpanded(false);
          if (chain.length > 0) {
            setStashedChain(chain);
            onChange([]);
          }
        }}
      >
        <Signature
          className={cn(
            "mt-0.5 h-5 w-5 shrink-0",
            !isSendingToOthers ? "text-primary" : "text-muted-foreground"
          )}
        />
        <span className="flex min-w-0 flex-col gap-1">
          <span className="text-sm font-medium">{copy.selfTitle}</span>
          <span className="text-sm leading-snug text-muted-foreground">{copy.selfDescription}</span>
        </span>
      </button>
      <button
        type="button"
        aria-label={copy.addOthers}
        aria-pressed={isSendingToOthers}
        className={cn(
          "flex min-h-24 items-start gap-3 rounded-xl border-2 p-4 text-left transition-colors",
          isSendingToOthers
            ? "border-primary bg-primary/[0.03]"
            : "border-border hover:border-muted-foreground/40 hover:bg-muted/30",
          "disabled:cursor-not-allowed disabled:opacity-50"
        )}
        disabled={disabled || !hasProject}
        onClick={() => {
          setExpanded(true);
          if (chain.length === 0 && stashedChain.length > 0) {
            onChange(stashedChain);
            setStashedChain([]);
          }
        }}
      >
        <Send
          className={cn(
            "mt-0.5 h-5 w-5 shrink-0",
            isSendingToOthers ? "text-primary" : "text-muted-foreground"
          )}
        />
        <span className="flex min-w-0 flex-col gap-1">
          <span className="text-sm font-medium">{copy.sendTitle}</span>
          <span className="text-sm leading-snug text-muted-foreground">{copy.sendDescription}</span>
        </span>
      </button>
    </div>
  );

  if (!hasProject) {
    return (
      <div className="flex flex-col gap-2.5">
        {modePicker}
        <p className="text-xs text-muted-foreground">{copy.needProject}</p>
      </div>
    );
  }

  // Signing alone is the common case and the shipped one, so the dialog must
  // look like it did before chaining existed: container name, Sign. A picker
  // sitting open reads as a step you have to deal with — and worse, as if you
  // were expected to add YOURSELF. So with an empty chain this collapses to one
  // line of opt-in, and pressing Sign right past it signs alone, exactly as on
  // develop.
  if (!isSendingToOthers) return modePicker;

  return (
    <div className="flex flex-col gap-4">
      {modePicker}
      <section className="flex flex-col gap-3 border-t pt-4" aria-labelledby="signer-chain-label">
        {/* No `htmlFor`: this names the SECTION (via its aria-labelledby), and the
            picker below is a `role="combobox"` button, not a labelable control. */}
        <Label
          id="signer-chain-label"
          className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
        >
          {copy.label}
        </Label>
        <UserMultiSelect
          users={users}
          selectedUserIds={selectedIds}
          onChange={(ids) => onChange(syncChain(chain, ids))}
          disabled={disabled}
          placeholder={isLoading ? copy.loading : copy.placeholder}
          searchPlaceholder={copy.search}
          emptyMessage={copy.none}
          className="h-11 bg-background"
          showNames
        />

        {chain.length > 0 && (
          <div className="flex flex-col gap-2">
            <h4
              id="signer-order-heading"
              className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              {copy.order}
            </h4>
            <p className="text-xs text-muted-foreground">{copy.youSignFirst}</p>
            {/* Not a tab stop: reordering is the Move up / Move down buttons,
                which are tab stops of their own. There is no drag here, so a
                focusable list would take a keyboard user nowhere. */}
            <ol aria-labelledby="signer-order-heading" className="flex flex-col gap-2">
              <li className="flex min-w-0 items-center gap-3 rounded-lg bg-muted/50 px-3 py-2.5 text-sm">
                <span className="w-14 shrink-0 text-xs font-medium text-muted-foreground">
                  {t.signing.chain.step.replace("{position}", "1")}
                </span>
                <UserAvatar user={currentUser} size={32} />
                <span className="min-w-0 flex-1 truncate font-medium">
                  {copy.you}
                  <span className="font-normal text-muted-foreground">
                    {` (${signerDisplayName(currentUser, t.signing.panel.signerUnknown)})`}
                  </span>
                </span>
              </li>
              {chain.map((group, index) => (
                <SignerStepRow
                  key={group.join("-")}
                  group={group}
                  index={index}
                  usersById={byId}
                  chain={chain}
                  onChange={onChange}
                  disabled={disabled}
                />
              ))}
            </ol>
          </div>
        )}

        {chain.length === 0 && <p className="text-xs text-muted-foreground">{copy.hint}</p>}
        {atCap && (
          <p className="text-xs text-muted-foreground">
            {copy.max.replace("{count}", String(MAX_CHAIN_INVITEES))}
          </p>
        )}
      </section>
    </div>
  );
}

interface SignerStepProps {
  group: number[];
  index: number;
  usersById: Map<number, UserProfile>;
  chain: SignerChain;
  onChange: (chain: SignerChain) => void;
  disabled: boolean;
}

function SignerStepRow(props: SignerStepProps) {
  const { t } = useLanguage();
  const { group, index, usersById, chain, onChange, disabled } = props;
  return (
    <li className="flex min-w-0 items-start gap-3 rounded-lg bg-muted/50 px-3 py-2.5 text-sm">
      <span className="mt-2 w-14 shrink-0 text-xs font-medium text-muted-foreground">
        {t.signing.chain.step.replace("{position}", String(index + CHAIN_POSITION_OFFSET))}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {group.map((userId, userIndex) => (
          <SignerIdentityRow
            key={userId}
            user={usersById.get(userId)}
            userId={userId}
            groupSize={group.length}
            chain={chain}
            onChange={onChange}
            disabled={disabled}
            stepControls={userIndex === 0 ? <SignerStepControls {...props} /> : undefined}
          />
        ))}
        {group.length > 1 && (
          <Badge variant="secondary" className="w-fit">
            {t.signing.dialog.signers.parallel}
          </Badge>
        )}
      </div>
    </li>
  );
}

interface SignerIdentityProps {
  user: UserProfile | undefined;
  userId: number;
  groupSize: number;
  chain: SignerChain;
  onChange: (chain: SignerChain) => void;
  disabled: boolean;
  stepControls?: ReactNode;
}

function SignerIdentityRow(props: SignerIdentityProps) {
  const { t } = useLanguage();
  const copy = t.signing.dialog.signers;
  const { user, userId, groupSize, chain, onChange, disabled, stepControls } = props;
  return (
    <div className="flex min-w-0 items-center gap-2">
      <UserAvatar user={user} size={32} />
      <span className="min-w-0 flex-1 truncate font-medium">
        {signerDisplayName(user, t.signing.panel.signerUnknown)}
      </span>
      {stepControls}
      {groupSize > 1 && (
        <StepIconButton
          label={copy.split}
          disabled={disabled}
          onClick={() => onChange(splitSigner(chain, userId))}
          icon={<Unlink className="h-3.5 w-3.5" />}
        />
      )}
      <StepIconButton
        label={copy.remove}
        disabled={disabled}
        onClick={() => onChange(removeSigner(chain, userId))}
        icon={<X className="h-4 w-4" />}
      />
    </div>
  );
}

function SignerStepControls({ index, chain, onChange, disabled }: SignerStepProps) {
  const copy = useLanguage().t.signing.dialog.signers;
  return (
    <div className="flex shrink-0 items-center">
      <StepIconButton
        label={copy.mergeUp}
        disabled={disabled || index === 0}
        onClick={() => onChange(mergeWithPrevious(chain, index))}
        icon={<Link2 className="h-3.5 w-3.5" />}
      />
      <StepIconButton
        label={copy.moveUp}
        disabled={disabled || index === 0}
        onClick={() => onChange(moveGroup(chain, index, -1))}
        icon={<ChevronUp className="h-4 w-4" />}
      />
      <StepIconButton
        label={copy.moveDown}
        disabled={disabled || index === chain.length - 1}
        onClick={() => onChange(moveGroup(chain, index, 1))}
        icon={<ChevronDown className="h-4 w-4" />}
      />
    </div>
  );
}

interface StepIconButtonProps {
  label: string;
  disabled: boolean;
  onClick: () => void;
  icon: ReactNode;
}

function StepIconButton({ label, disabled, onClick, icon }: StepIconButtonProps) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-8 w-8 shrink-0"
      disabled={disabled}
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      {icon}
    </Button>
  );
}
